import { useRef, useState } from 'react';
import { Plus, Loader2, Upload, X } from 'lucide-react';
import FileText from '/assets/sources/filetext.svg';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { useCreateSource } from '@/hooks/use-sources';
import type { CreateSourceDto } from '@/types';

type LinkMode = 'single' | 'bulk';

interface LinkRow {
  url: string;
  title: string;
}

const ALLOWED_FILE_TYPES = ['.txt'];

function ensureHttps(url: string): string {
  return `https://${url.trim().replace(/^https?:\/\//, '')}`;
}

function normalizeBulkUrl(rawUrl: string): string | null {
  const trimmedUrl = rawUrl.trim();
  if (!trimmedUrl || /\s/.test(trimmedUrl)) return null;

  const normalizedUrl = ensureHttps(trimmedUrl);
  try {
    const parsedUrl = new URL(normalizedUrl);
    return parsedUrl.hostname ? normalizedUrl : null;
  } catch {
    return null;
  }
}

const EMPTY_ROW: LinkRow = { url: '', title: '' };

interface AddLinksDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded?: () => void;
}

export function AddLinksDialog({ open, onOpenChange, onAdded }: AddLinksDialogProps) {
  const [mode, setMode] = useState<LinkMode>('single');
  const [rows, setRows] = useState<LinkRow[]>([{ ...EMPTY_ROW }]);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const createSourceMutation = useCreateSource();

  const reset = () => {
    setMode('single');
    setRows([{ ...EMPTY_ROW }]);
    setBulkFile(null);
    setIsDragging(false);
    setIsSubmitting(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) reset();
    onOpenChange(isOpen);
  };

  const updateRow = (index: number, patch: Partial<LinkRow>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_FILE_TYPES.includes(extension)) {
      toast.error(`Unsupported file format. Only ${ALLOWED_FILE_TYPES.join(', ')} are supported.`);
      return;
    }
    setBulkFile(file);
  };

  const handleAddSingleLinks = async () => {
    const validRows = rows.filter((r) => r.url.trim());
    if (validRows.length === 0) {
      toast.error('Please enter at least one valid URL');
      return;
    }
    setIsSubmitting(true);
    try {
      for (const row of validRows) {
        const url = ensureHttps(row.url);
        const sourceData: CreateSourceDto = {
          name: row.title.trim() || url,
          type: 'LINK',
          value: url
        };
        await createSourceMutation.mutateAsync(sourceData);
      }
      toast.success(`Successfully added ${validRows.length} link(s)`);
      onAdded?.();
      handleOpenChange(false);
    } catch (error) {
      toast.error('Failed to add links');
      console.error('Add links error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Bulk upload: a TXT file containing comma-separated links.
  const handleAddBulkLinks = async () => {
    if (!bulkFile) {
      toast.error('Please upload a .txt file containing comma-separated links');
      return;
    }
    setIsSubmitting(true);
    try {
      const text = await bulkFile.text();
      const rawUrls = text
        .split(',')
        .map((raw) => raw.trim())
        .filter(Boolean);

      if (rawUrls.length === 0) {
        toast.error('No comma-separated links found in the uploaded .txt file');
        return;
      }

      const normalizedUrls = rawUrls.map(normalizeBulkUrl);
      const validNormalizedUrls = normalizedUrls.filter((url): url is string => url !== null);
      const urls = [...new Set(validNormalizedUrls)];
      const invalidUrlCount = normalizedUrls.length - validNormalizedUrls.length;

      if (urls.length === 0) {
        toast.error('No valid comma-separated links found in the uploaded .txt file');
        return;
      }

      let addedCount = 0;
      const failedUrls: string[] = [];

      for (const url of urls) {
        const sourceData: CreateSourceDto = {
          name: url,
          type: 'LINK',
          value: url
        };

        try {
          await createSourceMutation.mutateAsync(sourceData);
          addedCount += 1;
        } catch (error) {
          failedUrls.push(url);
          console.error(`Failed to add bulk link: ${url}`, error);
        }
      }

      if (addedCount === 0) {
        toast.error('Failed to add links from the uploaded file');
        return;
      }

      toast.success(`Successfully added ${addedCount} link(s)`);
      const failedCount = failedUrls.length + invalidUrlCount;
      if (failedCount > 0) {
        toast.error(`${failedCount} invalid or failed link(s) were skipped`);
      }
      onAdded?.();
      handleOpenChange(false);
    } catch (error) {
      toast.error('Failed to add links');
      console.error('Add bulk links error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='w-[calc(100vw-2rem)] max-w-lg p-4'>
        <DialogHeader>
          <DialogTitle className='text-sm font-semibold'>Add Website Links</DialogTitle>
        </DialogHeader>

        <div className='flex gap-1 self-start rounded-md bg-gray-200 p-0.5'>
          <button
            type='button'
            onClick={() => setMode('single')}
            className={cn(
              'px-3 py-1 rounded-sm text-xs cursor-pointer',
              mode === 'single' ? 'bg-white font-semibold' : 'text-muted-foreground'
            )}
          >
            Single Link
          </button>
          <button
            type='button'
            onClick={() => setMode('bulk')}
            className={cn(
              'px-3 py-1 rounded-sm text-xs cursor-pointer',
              mode === 'bulk' ? 'bg-white font-semibold' : 'text-muted-foreground'
            )}
          >
            Bulk Upload
          </button>
        </div>

        {mode === 'single' ? (
          <div className='flex flex-col gap-4 max-h-[360px] overflow-y-auto'>
            {rows.map((row, index) => (
              <div key={index} className='flex flex-col gap-3'>
                {rows.length > 1 && (
                  <div className='flex justify-end'>
                    <Button variant='ghost' size='icon-sm' onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}>
                      <X className='size-4' />
                    </Button>
                  </div>
                )}
                <div>
                  <p className='text-xs font-medium mb-1.5'>URL</p>
                  <Input
                    type='text'
                    value={row.url}
                    onChange={(e) => updateRow(index, { url: e.target.value.replace(/^https?:\/\//, '') })}
                    placeholder='www.xyz.com'
                    className='text-sm'
                  />
                </div>
                <div>
                  <p className='text-xs font-medium mb-1.5'>
                    Display Title <span className='text-muted-foreground font-normal'>(Optional)</span>
                  </p>
                  <Input
                    type='text'
                    value={row.title}
                    onChange={(e) => updateRow(index, { title: e.target.value })}
                    placeholder='Enter display Title'
                    className='text-sm'
                  />
                </div>
                {index < rows.length - 1 && <div className='border-t' />}
              </div>
            ))}
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='self-start text-xs border-gray-300'
              onClick={() => setRows((prev) => [...prev, { ...EMPTY_ROW }])}
            >
              <Plus className='size-4' /> Add Another
            </Button>
          </div>
        ) : (
          <div className='flex flex-col gap-3'>
            <div
              className={cn(
                'h-[180px] border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-3.5 transition-colors cursor-pointer',
                isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
              )}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragging(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                handleFile(e.dataTransfer.files?.[0]);
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <img src={FileText} alt='' className='size-8' />
              <div className='text-center'>
                <p className='font-semibold text-sm'>Upload a TXT file with links</p>
                <p className='text-xs text-muted-foreground'>Only .txt files are supported. Separate each link with a comma.</p>
              </div>
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='text-xs border-gray-300'
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                <Upload className='size-4' /> Upload
              </Button>
              <input
                ref={fileInputRef}
                type='file'
                accept={ALLOWED_FILE_TYPES.join(',')}
                onChange={(e) => {
                  handleFile(e.target.files?.[0]);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className='hidden'
              />
            </div>

            {bulkFile && (
              <div className='flex flex-col gap-3 rounded-lg border p-3'>
                <div className='flex items-center justify-between gap-2'>
                  <div className='min-w-0'>
                    <p className='text-xs font-medium truncate'>{bulkFile.name}</p>
                    <p className='text-[11px] text-muted-foreground'>{(bulkFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                  </div>
                  <Button variant='ghost' size='icon-sm' onClick={() => setBulkFile(null)}>
                    <X className='size-4' />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className='flex gap-2 justify-end mt-2'>
          <Button type='button' variant='outline' className='text-xs' onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type='button'
            className='text-xs'
            onClick={mode === 'single' ? handleAddSingleLinks : handleAddBulkLinks}
            disabled={isSubmitting || (mode === 'single' ? rows.every((r) => !r.url.trim()) : !bulkFile)}
          >
            {isSubmitting ? <Loader2 className='size-4 animate-spin' /> : null}
            Add Links
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
